import React from 'react';
import { Button } from '@uplift/ui-v2';
import { StateExhibitSigned } from '@uplift/api';
import { useAgreementTemplateQuery, useProviderLicensesStatusQuery, useStateQuery } from '@uplift/queries';
import { Box, Divider, Table, TableBody, TableRow, Typography } from '@mui/material';
import { FileDownload } from '@mui/icons-material';
import { CalendarDateService } from '@uplift/services';
import { DateFormats } from '@uplift/constants';
import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { AgreementText, DetailsCell, DocumentLayout } from './components';
import { useDownloadStateExhibit } from './useDownloadStateExhibit';


export const StateExhibitSignedPreview: React.VFC<{
  document: StateExhibitSigned;
}> = ({
  document,
}) => {
  const me = useCurrentuser();
  const timezone = useTimezone();
  const { isDownloading, downloadExhibit } = useDownloadStateExhibit();
  const { data: template } = useAgreementTemplateQuery(document.agreementTemplateId);
  const { data: licenses } = useProviderLicensesStatusQuery(me.id, { suspense: true });
  const license = licenses?.find(l => l.id === document.licenseId);
  const { data: state } = useStateQuery(license?.stateId, { suspense: true });

  return (
    <DocumentLayout
      title={template.name}
      subtitle="Sent by BetterHelp"
      controls={(
        <Box>
          <Divider />
          <Box sx={{ p: 3, pt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              color="secondary"
              disabled={isDownloading}
              startIcon={<FileDownload />}
              onClick={() => {
                const statePart = (state?.fullName ?? 'state').replace(/\s+/g, '_');
                const filename = `${statePart}_state_exhibit_${CalendarDateService.date(document.signedAt).format('MM_DD_YYYY')}.pdf`;

                downloadExhibit(document.id, filename);
              }}
            >
              {isDownloading ? 'Preparing...' : 'Download'}
            </Button>
          </Box>
        </Box>
      )}
    >
      <Typography variant="overline" color="secondary">AGREEMENT DETAILS</Typography>
      <Table size="small" sx={{ 'tr:last-of-type td': { borderBottom: 0 } }}>
        <TableBody>
          <TableRow>
            <DetailsCell label="State Covered">{state?.fullName ?? '-'}</DetailsCell>
            <DetailsCell label="Status">Active</DetailsCell>
          </TableRow>
          <TableRow>
            <DetailsCell label="Effective Date">{CalendarDateService.date(document.signedAt).tz(timezone).format('MMM D, YYYY')}</DetailsCell>
            <DetailsCell label="Expires">{CalendarDateService.date(document.expireAt, DateFormats.ISO_DATE).format('MMM D, YYYY')}</DetailsCell>
          </TableRow>
        </TableBody>
      </Table>

      <AgreementText content={document.content} />
    </DocumentLayout>
  );
};
